import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Eye, EyeOff, ShieldCheck, AlertCircle, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO, publicUrl } from "@/lib/seo";

const AGE_OPTIONS = [
  { id: "toddler", label: "Toddler (under 3)" },
  { id: "preschool", label: "Preschool (3–5)" },
  { id: "school-age", label: "School-age (6–12)" },
  { id: "teen", label: "Teen (13–17)" },
  { id: "adult", label: "Adult (18+)" },
];

export default function FamilyProfileEditor() {
  const [data, setData] = useState({
    caregiver_first_name: "",
    caregiver_story: "",
    include_child_first_name: false,
    child_first_name: "",
    include_child_age: false,
    child_age_range: "",
    is_published: false,
  });
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState("");

  useSEO({ title: "My family story", description: "Edit your public family profile.", noindex: true });

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get("/family-profile/me");
        if (r.profile) {
          setExisting(r.profile);
          setData({
            caregiver_first_name: r.profile.caregiver_first_name || "",
            caregiver_story: r.profile.caregiver_story || "",
            include_child_first_name: !!r.profile.include_child_first_name,
            child_first_name: r.profile.child_first_name || "",
            include_child_age: !!r.profile.include_child_age,
            child_age_range: r.profile.child_age_range || "",
            is_published: !!r.profile.is_published,
          });
        }
      } catch {/* fresh form */}
    })();
  }, []);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const save = async (publishOverride) => {
    setSaving(true); setError(null); setInfo("");
    const body = { ...data };
    if (typeof publishOverride === "boolean") body.is_published = publishOverride;
    if (!body.include_child_first_name) body.child_first_name = null;
    if (!body.include_child_age) body.child_age_range = null;
    try {
      const { data: r } = await api.put("/family-profile/me", body);
      setExisting(r.profile);
      setInfo(r.profile.is_published ? "Your story is live ✨" : "Saved as a draft.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.findings) {
        setError(detail);
      } else {
        setError({ message: formatApiErrorDetail(detail) || e.message });
      }
    }
    setSaving(false);
  };

  const takedown = async () => {
    if (!window.confirm("Take your profile down? It'll be removed from sonspeak.com immediately. You can republish any time.")) return;
    try {
      await api.delete("/family-profile/me");
      setExisting(null);
      setData({
        caregiver_first_name: "", caregiver_story: "",
        include_child_first_name: false, child_first_name: "",
        include_child_age: false, child_age_range: "",
        is_published: false,
      });
      setInfo("Your profile has been taken down.");
    } catch (e) {
      setError({ message: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const url = existing ? `${publicUrl()}/families/${existing.slug}` : null;

  return (
    <div className="min-h-screen dotted-bg" data-testid="family-editor-page">
      <header className="px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3">
        <Link to="/profiles" className="pill-btn"><ArrowLeft size={16} /> Back</Link>
        <div className="display text-lg sm:text-xl font-extrabold">My family story</div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Celebratory opt-in card */}
        <section className="brutal-card bg-[color:var(--cream-deep)] p-5 mb-5">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl border-[3px] border-[color:var(--ink)] bg-[color:var(--coral)] text-white flex items-center justify-center">
              <Heart size={20} fill="currentColor" />
            </span>
            <div>
              <h2 className="display text-xl font-extrabold leading-tight">Share your story, help other families find their way.</h2>
              <p className="text-sm text-[color:var(--ink-soft)] mt-0.5">
                Your words can be the lifeline another parent finds at 2am. We'll keep your child's identity protected — that part isn't optional.
              </p>
            </div>
          </div>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h3 className="display text-lg font-bold mb-3">About you</h3>
          <label className="block mb-2 text-sm font-semibold">Your first name</label>
          <input
            type="text"
            className="brutal-input w-full mb-4"
            value={data.caregiver_first_name}
            onChange={(e) => update({ caregiver_first_name: e.target.value })}
            placeholder="e.g. Mary"
            maxLength={40}
            data-testid="family-caregiver-name"
          />

          <label className="block mb-2 text-sm font-semibold">Your story (20–2000 chars)</label>
          <textarea
            rows={8}
            className="brutal-input w-full font-sans"
            value={data.caregiver_story}
            onChange={(e) => update({ caregiver_story: e.target.value })}
            placeholder="What was your AAC journey? What works? What would you tell another parent who just found out their child is nonverbal?"
            maxLength={2000}
            data-testid="family-story"
          />
          <div className="text-xs text-[color:var(--ink-soft)] mt-1 text-right">
            {data.caregiver_story.length}/2000
          </div>
        </section>

        <section className="brutal-card bg-white p-5 mb-5">
          <h3 className="display text-lg font-bold mb-1">About your child (optional)</h3>
          <p className="text-sm text-[color:var(--ink-soft)] mb-4">
            By default we keep your child completely anonymous. Most parents leave these off.
          </p>

          <label className="flex items-start gap-3 cursor-pointer mb-3" data-testid="toggle-child-name">
            <input
              type="checkbox"
              checked={data.include_child_first_name}
              onChange={(e) => update({ include_child_first_name: e.target.checked })}
              className="w-5 h-5 mt-0.5 accent-[color:var(--coral)]"
            />
            <span>
              <span className="font-semibold">Include my child's first name</span>
              <span className="block text-xs text-[color:var(--ink-soft)]">
                First name only — no last names, ever.
              </span>
            </span>
          </label>
          {data.include_child_first_name && (
            <input
              type="text"
              className="brutal-input w-full mb-4"
              value={data.child_first_name}
              onChange={(e) => update({ child_first_name: e.target.value })}
              placeholder="e.g. Sammy"
              maxLength={24}
              data-testid="family-child-name"
            />
          )}

          <label className="flex items-start gap-3 cursor-pointer mb-3" data-testid="toggle-child-age">
            <input
              type="checkbox"
              checked={data.include_child_age}
              onChange={(e) => update({ include_child_age: e.target.checked })}
              className="w-5 h-5 mt-0.5 accent-[color:var(--coral)]"
            />
            <span>
              <span className="font-semibold">Include child's age range</span>
              <span className="block text-xs text-[color:var(--ink-soft)]">
                Range only (no exact ages). Helps other parents in similar life stages find your story.
              </span>
            </span>
          </label>
          {data.include_child_age && (
            <select
              className="brutal-input w-full"
              value={data.child_age_range || ""}
              onChange={(e) => update({ child_age_range: e.target.value })}
              data-testid="family-child-age"
            >
              <option value="">Pick a range…</option>
              {AGE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          )}
        </section>

        <section className="brutal-card bg-[#F5F0FF] p-4 mb-5 flex items-start gap-2 text-sm">
          <ShieldCheck size={18} className="shrink-0 mt-0.5 text-[#6D28D9]" />
          <div>
            We block last names, school names, addresses, phone numbers, and city/state references — even if you write them. Family profile pages also opt out of search-engine indexing. Take your profile down any time, instant.
          </div>
        </section>

        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-4 mb-5" data-testid="family-error">
            <div className="flex items-center gap-2 font-bold mb-2">
              <AlertCircle size={16} /> {error.message || "Couldn't save."}
            </div>
            {error.findings && error.findings.length > 0 && (
              <ul className="list-disc pl-5 text-sm">
                {error.findings.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}
            {error.suggestion && <div className="text-sm mt-2 italic">{error.suggestion}</div>}
          </div>
        )}
        {info && (
          <div className="brutal-card bg-[#E6F8E1] p-3 text-sm mb-5" data-testid="family-info">{info}</div>
        )}

        <div className="flex flex-wrap gap-2 mb-5">
          {data.is_published ? (
            <button type="button" onClick={() => save(false)} disabled={saving} className="pill-btn" data-testid="family-unpublish">
              <EyeOff size={14} /> Unpublish
            </button>
          ) : (
            <button type="button" onClick={() => save(true)} disabled={saving} className="pill-btn primary" data-testid="family-publish">
              <Eye size={14} /> Publish my story
            </button>
          )}
          <button type="button" onClick={() => save()} disabled={saving} className="pill-btn" data-testid="family-save-draft">
            Save as draft
          </button>
          {existing && (
            <button type="button" onClick={takedown} className="pill-btn danger" data-testid="family-takedown">
              <Trash2 size={14} /> Take down
            </button>
          )}
        </div>

        {existing && existing.is_published && url && (
          <div className="brutal-card bg-[color:var(--paper-muted)] p-3 text-sm" data-testid="family-public-url">
            Live at: <a href={url} target="_blank" rel="noreferrer" className="font-mono text-[color:var(--coral-deep)] underline break-all">{url}</a>
          </div>
        )}
      </main>
    </div>
  );
}
